<script setup>
import { computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useThemeColors } from 'dashboard/composables/useThemeColors';
import DonutChart from 'shared/components/charts/DonutChart.vue';

// eslint-disable-next-line no-unused-vars
const store = useStore();
const agentSummaryData = useMapGetter('summaryReports/getAgentSummaryReports');
const { getChartColors } = useThemeColors();

const chartData = computed(() => {
  const dataToUse = agentSummaryData.value;

  if (!dataToUse || dataToUse.length === 0) {
    return { labels: [], datasets: [] };
  }

  const totalConversations = dataToUse.reduce((sum, agent) => {
    return sum + (agent.conversationsCount || 0);
  }, 0);

  if (totalConversations === 0) {
    return { labels: [], datasets: [] };
  }

  const agentsWithConversations = dataToUse
    .filter(agent => (agent.conversationsCount || 0) > 0)
    .map(agent => ({
      name: agent.name || 'Agente sem nome',
      count: agent.conversationsCount || 0,
    }))
    .sort((a, b) => b.count - a.count);

  const chartColors = getChartColors();
  const themePalette = chartColors.chartPalette || [];
  const backgroundColors = agentsWithConversations.map((_, idx) =>
    themePalette.length ? themePalette[idx % themePalette.length] : '#1C7CFC'
  );

  return {
    labels: agentsWithConversations.map(agent => agent.name),
    datasets: [
      {
        data: agentsWithConversations.map(agent => agent.count),
        backgroundColor: backgroundColors,
        borderWidth: 2,
        borderColor: chartColors.background,
      },
    ],
  };
});

const chartOptions = computed(() => {
  const chartColors = getChartColors();

  return {
    cutout: '60%',
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels: chart => {
            const data = chart.data;
            if (data.labels && data.datasets && data.datasets[0]) {
              return data.labels.map((label, index) => ({
                text: `${label}: ${data.datasets[0].data[index]}`,
                fillStyle: data.datasets[0].backgroundColor[index],
                strokeStyle:
                  data.datasets[0].borderColor || chartColors.background,
                lineWidth: data.datasets[0].borderWidth || 1,
                hidden: false,
                index: index,
                fontColor: chartColors.textPrimary,
              }));
            }
            return [];
          },
          padding: 10,
          boxWidth: 12,
          font: {
            size: 12,
          },
          color: chartColors.textPrimary,
          usePointStyle: false,
        },
      },
      tooltip: {
        backgroundColor: chartColors.tooltipBg,
        titleColor: chartColors.textPrimary,
        bodyColor: chartColors.textSecondary,
        borderColor: chartColors.tooltipBorder,
        borderWidth: 1,
        callbacks: {
          label: context => {
            const label = context.label || '';
            const value = context.parsed || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
  };
});

const hasData = computed(() => {
  return chartData.value.labels.length > 0;
});
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl p-4 shadow outline-1 outline outline-n-container w-full max-w-full"
  >
    <h3 class="text-lg font-medium text-n-slate-12 mb-4 text-center">
      {{ $t('REPORT.AGENT_ATTENDANCE_CHART_TITLE') }}
    </h3>
    <div v-if="hasData" class="h-72 w-full">
      <DonutChart :collection="chartData" :chart-options="chartOptions" />
    </div>
    <div v-else class="flex items-center justify-center h-72">
      <span class="text-sm text-slate-600">
        {{ $t('REPORT.NO_ENOUGH_DATA') }}
      </span>
    </div>
  </div>
</template>
